import { useState } from 'react';
import { getModelGender, getUnitPrice, PRICING, type DeliveryWeeks } from '../utils/pricing';

const DELIVERIES: { weeks: DeliveryWeeks; label: string }[] = [
  { weeks: '8mie', label: '8 semaines Made In Europe' },
  { weeks: 8,      label: '8 semaines'                },
  { weeks: 14,     label: '14 semaines'               },
  { weeks: 16,     label: '16 semaines'               },
];

type ModelLike = { fileName?: string; category?: string; name?: string } | null;

type BottomBarProps = {
  onExport: (qty: number) => void;
  onShareCreation: () => void;
  onPriceSimulator: () => void;
  isReadOnly: boolean;
  isExporting: boolean;
  activeModel?: ModelLike;
};

export default function BottomBar({
  onExport,
  onShareCreation,
  onPriceSimulator,
  isReadOnly,
  isExporting,
  activeModel,
}: BottomBarProps) {
  const [qty, setQty] = useState(30);
  const [rawQty, setRawQty] = useState('30');

  const gender = getModelGender(activeModel ?? null);
  const ref = PRICING[gender].ref;

  const handleQtyChange = (val: string) => {
    setRawQty(val);
    const n = parseInt(val, 10);
    if (!isNaN(n) && n > 0) setQty(n);
  };

  return (
    <div className="bottom-bar">
      {/* ── Gauche : Sauvegarder ── */}
      <div className="bottom-left">
        {!isReadOnly && (
          <button className="bottom-btn" type="button" onClick={onShareCreation}>
            <img src="/assets/icon/icons8-export-50.png" alt="" aria-hidden="true" />
            Sauvegarder / Partager la création
          </button>
        )}
      </div>

      {/* ── Centre : simulateur inline ── */}
      <div className="bottom-pricing">
        <div className="bp-qty">
          <label className="bp-label" htmlFor="bp-qty-input">Qté</label>
          <input
            id="bp-qty-input"
            type="number"
            min={1}
            className="bp-qty-input"
            value={rawQty}
            onChange={e => handleQtyChange(e.target.value)}
            onBlur={() => setRawQty(String(qty))}
          />
        </div>

        <div className="bp-divider" />

        {DELIVERIES.map(({ weeks, label }) => {
          const price = getUnitPrice(gender, weeks, qty);
          const total = price != null ? price * qty : null;
          return (
            <div key={weeks} className="bp-tier">
              <span className="bp-tier-label">{label}</span>
              <span className="bp-tier-price">
                {price != null ? `${price.toFixed(2)} €` : '—'}
              </span>
              <span className="bp-tier-total">
                {total != null
                  ? total.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €'
                  : '—'}
              </span>
            </div>
          );
        })}

        <div className="bp-divider" />

        {/* Bouton "Voir plus" → modal détaillé */}
        <button
          type="button"
          className="bp-detail-btn"
          onClick={onPriceSimulator}
          title={`Réf. ${ref} · Voir la grille complète`}
        >
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M10 2a8 8 0 1 1 0 16A8 8 0 0 1 10 2Z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 9v5M10 7v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          Voir les tarifs
        </button>
      </div>

      {/* ── Droite : Terminer ── */}
      <div className="bottom-right">
        <button
          className="bottom-cta"
          type="button"
          onClick={() => onExport(qty)}
          disabled={isExporting}
          data-tutorial="finish-btn"
        >
          {isExporting ? 'Export en cours…' : 'Terminer la configuration'}
        </button>
      </div>

      {/* ── Mobile : boutons pleins ── */}
      <div className={`bottom-mobile${isReadOnly ? ' bottom-mobile--readonly' : ''}`}>
        {!isReadOnly && (
          <button className="bottom-mobile-btn" type="button" onClick={onShareCreation}>
            Sauvegarder
          </button>
        )}
        <button className="bottom-mobile-price" type="button" onClick={onPriceSimulator}>
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" width="14" height="14">
            <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M7 9h6M7 12h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
          Tarifs
        </button>
        <button className="bottom-mobile-cta" type="button" onClick={() => onExport(qty)} disabled={isExporting}>
          Terminer
        </button>
      </div>
    </div>
  );
}
